/**
 * The shot on a machine with a Manetta board, as the guided brew follows it (#257 phase 2): what the store keeps
 * about it, when Status shows it over, and what to tell someone at the machine when it can't start or ended for a
 * reason that isn't Taretto. The store (store.ts) runs it: Start brew, the heartbeat, and stop_brew until it's over.
 */
import { COOLING_HINT } from './labels';
import type { MachineCommandResult, MachineInfo, MachineState, MachineStopReason } from './types';

/** start_brew asks for this cap: under the H10B's own 2 minutes, and longer than any shot. */
export const SHOT_MAX_MS = 90_000;
/** A ping this often while a shot Taretto started runs. The H10B's heartbeat_timeout_ms is 5 s, the protocol's least. */
export const PING_MS = 1000;
/** stop_brew again this often until Status shows the shot stopped (SPEC 5.5)... */
export const STOP_REPEAT_MS = 1000;
/** ...and past this without that, say to stop the machine by hand. */
export const STOP_CONFIRM_MS = 3000;
/** Sends of stop_brew before Taretto leaves it to the person (it said to stop by hand long before). */
export const STOP_MAX_SENDS = 30;
/** A brewing device quiet for longer than this is in trouble (SPEC 3.4). */
export const STATUS_QUIET_MS = 2000;

export interface MachineShot {
  /** Tells one shot from the next, for the loops that run on after it's replaced. */
  id: number;
  /** Date.now() when Taretto started or picked it up. */
  at: number;
  /** Start brew in Taretto, or picked up from Status (the machine's own button while the brew step waited). */
  startedBy: 'taretto' | 'machine';
  /** The device's boot and the brew, as Status reports them (SPEC 3.4). */
  bootId: number;
  brewId: number;
  /** running → stopping (Taretto sent stop_brew) → stopped (Status shows it over). */
  phase: 'running' | 'stopping' | 'stopped';
  /** Taretto asked the machine to stop it (it may have stopped by itself first). */
  stopAsked: boolean;
  /** Stopping has taken longer than STOP_CONFIRM_MS without Status showing it (SPEC 5.5). */
  stopLate: boolean;
  /** What stopped it, as the machine reported it. null while it runs, or when it never said. */
  stopReason: MachineStopReason | null;
  /**
   * The brew's timer started with this shot, so the machine's own times line up with it: Taretto started it (the
   * timer starts on the OK, a moment after the machine began counting), or the brew step picked it up and backdated
   * its timer by Status's elapsed_ms. Not one already running before the step waited, which a stop picks up.
   */
  timed: boolean;
  /**
   * How long the shot ran, as the machine reported it once it stopped (Status's elapsed_ms for the same boot and
   * brew). null while it runs, or when it never said.
   */
  stopElapsedMs: number | null;
  /** The device restarted (a new boot_id), which stopped the shot. */
  restarted: boolean;
  /** The link dropped while the shot ran. */
  linkLost: boolean;
  /** Status has been quiet for longer than STATUS_QUIET_MS while the shot runs. */
  quiet: boolean;
  /** The page went to the background while the shot ran (an iPad pauses it, and the heartbeat with it). */
  hidden: boolean;
}

/** Whether Status shows the shot over: stopped, a new boot, or another brew running in its place. */
export function shotOver(shot: Pick<MachineShot, 'bootId' | 'brewId'>, s: MachineState | null): boolean {
  if (!s) return false;
  if (s.bootId !== shot.bootId) return true;
  if (s.brewId !== shot.brewId) return s.phase === 'brewing';
  return s.phase !== 'brewing';
}

/** How long the brew Status shows brewing has run by `now`: its elapsed_ms, counted on from when that Status came. */
export function brewElapsedMs(s: Pick<MachineState, 'elapsedMs' | 'at'>, now = performance.now()): number {
  return s.elapsedMs + Math.max(0, now - s.at);
}

/**
 * When the machine says the shot stopped, in ms from the brew timer's start: its stopElapsedMs, for a shot the timer
 * started with. null for any other, and while it runs.
 */
export function shotStopMs(shot: MachineShot | null): number | null {
  return shot?.timed && shot.phase === 'stopped' ? shot.stopElapsedMs : null;
}

/** The shot still runs, as far as Taretto knows. */
export function shotRunning(shot: MachineShot | null): boolean {
  return shot != null && shot.phase !== 'stopped';
}

/** The connected machine can start a shot. */
export function canBrew(info: MachineInfo | null): boolean {
  return info?.capabilities.brew === true;
}

/**
 * Why the machine can't start a shot now, from its power and steam state; null when it shows ready (or can't tell
 * heating from ready, and the device answers for itself). `turnOn`: offer Turn on.
 */
export function startBlocker(state: MachineState | null): { text: string; turnOn: boolean } | null {
  if (!state) return { text: 'Waiting for the machine to report its state.', turnOn: false };
  if (state.phase === 'fault') return { text: 'The machine reports a fault. Check it before the next shot.', turnOn: false };
  if (state.phase === 'brewing') return { text: 'The machine is brewing.', turnOn: false };
  if (state.steam === 'heating' || state.steam === 'ready') return { text: 'The machine is in steam mode. Switch it back to brew first.', turnOn: false };
  switch (state.power) {
    case 'off':
      return { text: 'The machine is off.', turnOn: true };
    case 'heating':
      return { text: 'The machine is still heating.', turnOn: false };
    case 'cooling':
      return { text: COOLING_HINT, turnOn: false };
    default:
      return null;
  }
}

/** Why start_brew didn't start the shot, in words for someone at the machine: from its state when it's that. */
export function startProblem(result: MachineCommandResult, state: MachineState | null): string {
  switch (result) {
    case 'not_ready':
    case 'busy':
    case 'fault':
      return startBlocker(state)?.text ?? (result === 'busy' ? 'The machine is busy. Try again in a moment.' : result === 'fault' ? 'The machine reports a fault. Check it, then try again.' : 'The machine isn’t ready to brew yet. Try again in a moment.');
    case 'unsupported':
      return 'This machine can’t start a shot.';
    case 'invalid':
      return 'The machine didn’t take the command. Its firmware may not match this version of Taretto.';
    case 'no_reply':
      return 'The machine didn’t answer. Make sure it’s plugged in and nearby.';
    case 'ok':
      return '';
  }
}

/**
 * What went wrong with the shot, when something did: Status quiet, the link lost, the board restarted, or a stop
 * that wasn't Taretto's (after a reconnect, from the stop_reason Status gives). `tarettoStops`: Taretto was to stop
 * it (settings.machineStopsShot, on a shot with a target), so a stop on the machine came too early.
 */
export function shotProblem(shot: MachineShot | null, tarettoStops: boolean): string | null {
  if (!shot) return null;
  if (shot.phase !== 'stopped') {
    if (shot.linkLost) {
      return shot.startedBy === 'taretto'
        ? 'Lost the connection to the machine mid-shot. It stops the shot by itself.'
        : 'Lost the connection to the machine mid-shot. Stop the shot on the machine if it’s still running.';
    }
    if (shot.quiet) return 'The machine stopped reporting mid-shot. If it’s still running, stop it by hand.';
    return null;
  }
  if (shot.restarted) return 'The machine’s board restarted, which stopped the shot.';
  switch (shot.stopReason) {
    case 'machine':
      // Taretto was to stop it: the machine's programmed volume (or someone at it) came first.
      return tarettoStops && !shot.stopAsked ? 'The machine ended the shot by itself. If its programmed volume did, set it higher.' : null;
    case 'command_cap':
      return `The shot reached the ${SHOT_MAX_MS / 1000} s Taretto allows, so the machine stopped it.`;
    case 'device_cap':
      return 'The shot reached the machine’s own time limit, so it stopped.';
    case 'link_lost':
      return 'The connection to the machine dropped, which stopped the shot.';
    case 'heartbeat':
      return shot.hidden ? 'The shot stopped while Taretto was in the background.' : 'Taretto went quiet for the machine, so it stopped the shot.';
    case 'fault':
      return 'The machine reports a fault, which stopped the shot.';
    case 'app':
      return null;
    default:
      return shot.linkLost ? 'Lost the connection to the machine, so how the shot ended isn’t known.' : null;
  }
}
